import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PendingPaymentsSection — Admin dashboard section listing every
 * booking whose payment is still outstanding. Focus is on the
 * bank-transfer flow: admin can mark a booking as "paid" once the
 * funds actually land in their account.
 *
 * The component is self-loading; a caller only needs to drop
 * <PendingPaymentsSection /> where it wants the table to appear.
 */
const STATUS_LABELS = {
  pending_payment:       { label: 'Awaiting choice',   bg: 'hsl(45 95% 92%)',  fg: 'hsl(35 80% 30%)' },
  pending_mollie:        { label: 'Card in progress',  bg: 'hsl(220 60% 94%)', fg: 'hsl(220 60% 30%)' },
  pending_bank_transfer: { label: 'Bank transfer',     bg: 'hsl(35 90% 92%)',  fg: 'hsl(30 80% 30%)' },
};

const METHOD_LABEL = {
  mollie:        'Card',
  bank_transfer: 'Bank transfer',
};

export function PendingPaymentsSection() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.listPendingPaymentBookings();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async (row) => {
    const label = `${row.parent_name || row.parent_email || 'this parent'} — ${row.child_name || 'child'} (£${row.payment_amount.toFixed(2)})`;
    if (!window.confirm(`Confirm payment received for:\n\n${label}\n\nThis cannot be undone.`)) return;
    setConfirmingId(row._id);
    try {
      await api.confirmBookingPayment(row._id, null);
      toast.success('Payment marked as received.');
      setRows((prev) => prev.filter((r) => r._id !== row._id));
    } catch (err) {
      toast.error(err?.message || 'Failed to confirm payment');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className="card" data-testid="pending-payments-section" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0 }}>💰 Pending Payments</h2>
          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
            PAYG bookings awaiting card checkout or bank transfer confirmation.
          </small>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={load}
          disabled={loading}
          data-testid="pending-payments-refresh"
        >
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading" data-testid="pending-payments-loading">Loading…</div>
      ) : error ? (
        <div
          className="alert alert-error"
          data-testid="pending-payments-error"
          style={{
            background: 'hsl(0 70% 95%)', color: 'hsl(0 70% 35%)',
            padding: '0.6rem 0.85rem', borderRadius: 6, fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state" style={{ padding: '1.5rem' }} data-testid="pending-payments-empty">
          <p style={{ margin: 0 }}>All caught up 🎉 — no bookings awaiting payment.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="pending-payments-table">
            <thead>
              <tr style={{ borderBottom: '2px solid hsl(var(--border))', textAlign: 'left' }}>
                <th style={th}>Parent</th>
                <th style={th}>Child</th>
                <th style={th}>Session date</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={th}>Method</th>
                <th style={th}>Reference</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = STATUS_LABELS[r.payment_status] || { label: r.payment_status, bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' };
                return (
                  <tr key={r._id} style={{ borderTop: '1px solid hsl(var(--border))' }} data-testid={`pending-payment-row-${r._id}`}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.parent_name || '—'}</div>
                      <small style={{ color: 'hsl(var(--muted-foreground))' }}>{r.parent_email}</small>
                    </td>
                    <td style={td}>{r.child_name || '—'}</td>
                    <td style={td}>{r.session_date ? new Date(r.session_date).toLocaleDateString() : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      £{r.payment_amount.toFixed(2)}
                    </td>
                    <td style={td}>{METHOD_LABEL[r.payment_method] || <em style={{ color: 'hsl(var(--muted-foreground))' }}>not chosen</em>}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.payment_reference || '—'}</td>
                    <td style={td}>
                      <span className="badge" style={{ background: s.bg, color: s.fg, fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: 999, fontWeight: 600 }}>
                        {s.label}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={confirmingId === r._id}
                        onClick={() => handleConfirm(r)}
                        data-testid={`pending-payment-confirm-${r._id}`}
                        style={{ fontSize: '0.8rem' }}
                      >
                        {confirmingId === r._id ? 'Confirming…' : '✓ Confirm payment'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '0.5rem 0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.03em' };
const td = { padding: '0.6rem 0.5rem', verticalAlign: 'top' };

export default PendingPaymentsSection;
